import { Button, Modal, notification, Popconfirm, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { FieldData, Props } from './types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../../../store';
import api from '../../../../../../../../service/api';
import { SKW_PREFIX } from '../../../../../../../../utils/constants';
import CustomizedForm from './Form';

const App: React.FC<Props> = ({
  dataList,
  setDataList,
  data,
  handleDeleteItem,
  icon,
  anexos,
  setAnexos,
  handleDeleteAnexo,
}) => {
  const token = useSelector((state: ApplicationState) => state.auth.token);
  const [loadingEdit, setLoadingEdit] = useState<boolean>(false);
  const [loadingDelet, setLoadingDelet] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [disabled, setDisable] = useState<boolean>(icon === 'show' ? true : false);
  const [chaveNfe, setChaveNfe] = useState<string>(data?.chaveNfe);
  const [tipoVolume, setTipoVolume] = useState<string>(data?.tipoVolume);
  const [qtdVolume, setQtdVolume] = useState<number>(data?.qtdVolume);
  const [pesoBruto, setPesoBruto] = useState<any>(
    data?.pesoBruto?.toLocaleString('pt-BR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })
  );
  const [valor, setValor] = useState<any>(
    data?.valor?.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
  const [nomeCidadeOrigem, setNomeCidadeOrigem] = useState<any>(data?.cidadeOrigem);
  const [codCidadeOrigem, setCodCidadeOrigem] = useState<number>(data?.codCidadeOrigem);
  const [observacao, setObservacao] = useState<string>(data?.observacao);
  const [fields, setFields] = useState<FieldData[]>([{ name: [''], value: '' }]);

  useEffect(() => {
    setChaveNfe(data?.chaveNfe);
    setTipoVolume(data?.tipoVolume);
    setQtdVolume(data?.qtdVolume);

    setPesoBruto(
      data?.pesoBruto?.toLocaleString('pt-BR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })
    );
    setValor(
      data?.valor?.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
    setNomeCidadeOrigem(data?.cidadeOrigem);
    setCodCidadeOrigem(data?.codCidadeOrigem);
    setObservacao(data?.observacao);
  }, [data]);

  useEffect(() => {
    setFields([
      { name: ['chaveNfe'], value: chaveNfe },
      { name: ['tomador'], value: data?.tomador },
      { name: ['fornecedor'], value: data?.fornecedor },
      { name: ['tipoVolume'], value: tipoVolume },
      {
        name: ['qtdVolume'],
        value: qtdVolume,
      },
      { name: ['numNota'], value: data?.numNota },

      {
        name: ['pesoBruto'],
        value: pesoBruto.toLocaleString('pt-BR', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }),
      },

      {
        name: ['valor'],
        value: valor,
      },
      { name: ['cidadeOrigem'], value: nomeCidadeOrigem },
      { name: ['observacao'], value: observacao },
    ]);
  }, [
    chaveNfe,
    nomeCidadeOrigem,
    observacao,
    pesoBruto,
    qtdVolume,
    tipoVolume,
    valor,
    data,
    handleDeleteItem,
  ]);

  const openNotification = (message: string) => {
    notification.info({
      placement: 'bottomRight',
      message: message,
    });
  };

  const showModal = () => {
    setOpen(true);
    icon === 'show' ? setDisable(true) : setDisable(false);
  };

  const handleDelet = () => {
    setLoadingDelet(true);
    setTimeout(() => {
      setLoadingDelet(false);
      handleDeleteItem(data?.nuNota);
      setOpen(false);
    }, 1000);
  };

  const handleEdit = () => {
    setLoadingEdit(true);
    setTimeout(() => {
      setLoadingEdit(false);
      setDisable(false);
    }, 1000);
  };

  const handleSave = () => {
    setLoadingEdit(true);
    const pedido: any = {
      nuNota: data?.nuNota,
      chaveNfe: chaveNfe,
      valor: Number(valor?.toString().replace(/\./g, '').replace(',', '.')),
      pesoBruto: +pesoBruto?.toString().replace(/\./g, '').replace(',', '.'),
      codCidadeOrigem: codCidadeOrigem,
      observacao: observacao,
      tipoVolume: tipoVolume,
      qtdVolume: +qtdVolume?.toString().replace(/[^0-9]/g, ''),
    };
    api
      .post(`call?serviceName=${SKW_PREFIX}alteraPedidoEntrega&token=${token}`, {
        pedido,
      })
      .then((res: any) => {
        dataList.splice(
          dataList.findIndex((item: any) => item.nuNota === pedido.nuNota),
          1,
          res.data.pedido
        );
        setDataList([...dataList]);
        openNotification('Documento alterado com sucesso!');
        setLoadingEdit(false);
        setOpen(false);
      })
      .catch((error: any) => {
        setLoadingEdit(false);
        if (error.response) {
          return openNotification(`Ops, ${error.response.data.Erro}`);
        } else {
          // Alguma coisa acontenceu ao configurar a requisição que acionou este erro.
          return openNotification(`Ops, Não foi possível alterar o documento!`);
        }
      });
  };

  const handleCancel = () => {
    setOpen(false);
    setDisable(true);
  };

  return (
    <>
      <Tooltip title={icon === 'show' ? 'Ver detalhes' : 'Editar'}>
        <Button
          onClick={showModal}
          type="default"
          style={{ width: '25px', border: 'none' }}
          disabled={false}
          icon={icon === 'show' ? <EyeOutlined /> : <EditOutlined />}
        ></Button>
      </Tooltip>

      <Modal
        width={1050}
        visible={open}
        title="Detalhes do documento"
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            <ArrowLeftOutlined /> Voltar
          </Button>,

          disabled && (
            <Button key="edit" type="primary" loading={loadingEdit} onClick={handleEdit}>
              <EditOutlined /> Editar
            </Button>
          ),

          !disabled && (
            <Button type="primary" loading={loadingEdit} onClick={handleSave}>
              <SaveOutlined /> Salvar
            </Button>
          ),

          icon === 'show' && (
            <Popconfirm
              title="Deseja realmente excluir este documento?"
              onConfirm={handleDelet}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                key="submit"
                type="default"
                loading={loadingDelet}
                style={{ border: 'none', background: '#FF4D4F', color: '#fff' }}
              >
                <DeleteOutlined /> Excluir
              </Button>
            </Popconfirm>
          ),
        ]}
      >
        <CustomizedForm
          fields={fields}
          onChange={(newFields: React.SetStateAction<FieldData[]>) => {
            setFields(newFields);
          }}
          disabled={disabled}
          setChaveNfe={function (value: React.SetStateAction<string>): void {
            setChaveNfe(value);
          }}
          setTipoVolume={function (value: React.SetStateAction<string>): void {
            setTipoVolume(value);
          }}
          setQtdVolume={function (value: React.SetStateAction<number>): void {
            setQtdVolume(value);
          }}
          setPesoBruto={function (value: any): void {
            setPesoBruto(value);
          }}
          setValor={function (value: any): void {
            setValor(value);
          }}
          setNomeCidadeOrigem={function (value: React.SetStateAction<string>): void {
            setNomeCidadeOrigem(value);
          }}
          setCodCidadeOrigem={function (value: React.SetStateAction<number>): void {
            setCodCidadeOrigem(value);
          }}
          setObservacao={function (value: React.SetStateAction<string>): void {
            setObservacao(value);
          }}
          data={data}
          anexos={anexos}
          setAnexos={setAnexos}
          handleDeleteAnexo={handleDeleteAnexo}
        />
        {/* <pre className="language-bash">{JSON.stringify(fields, null, 2)}</pre> */}
      </Modal>
    </>
  );
};

export default App;
